export type TAccountPicklist = {
  accountId: string;
  name: string;
  accountStatus: AccountStatus;
};
export enum AccountStatus {
  Active = "Active",
  Inactive = "Inactive"
}

export type TBlankMarkerStyle = {
  fontName: string;
  fontStyle: string;
  fontSize?: number;
  fontColor: string;
  fontUnderLine: string;
  backgroundColor: string;
};

export type TReportUsers = {
  userId: string;
  accountId: string;
  contactId: number;
  firstName: string;
  lastName: string;
  isProvider: "Y" | "N";
};

export type TReportUserParameters = {
  userId: string;
  userName: string;
  locationId?: number;
};

export type TReportFields = {
  DATE_OF_DICTATION?: string | null;
  DATE_OF_DICTATION_FROM?: string | null;
  DATE_OF_DICTATION_TO?: string | null;
  DATE_OF_SERVICE?: string | null;
  DATE_OF_SERVICE_FROM?: string | null;
  DATE_OF_SERVICE_TO?: string | null;
  REGION?: string[];
  LOCATIONID?: string[];
  PROVIDER_ID?: string[];
  REPORT_TYPE?: string[];
  SPECIALTY_ID?: string[];
  USERID?: string[];
  AUDITEVENT?: string[];
  NUMBER_OF_DAYS?: number | null;
  DATE_OF_UPLOAD_FROM?: string | null;
  DATE_OF_UPLOAD_TO?: string | null;
  DATE_OF_FAX_FROM?: string | null;
  DATE_OF_FAX_TO?: string | null;
  PATIENT_NAME?: string;
  MRN?: string;
  CLIENT_NOTES?: string;
  SHOW_PATIENT_NAME?: string;
  STATUS?: string[];
  JOB_MONTH?: string[];
  JOB_YEAR?: string[];
};

export type TReportFieldKeys = keyof TReportFields;

export type TReportFilters = {
  dateOfDictation?: string | null;
  dateOfDictationFrom?: string | null;
  dateOfDictationTo?: string | null;
  dateOfService?: string | null;
  dateOfServiceFrom?: string | null;
  dateOfServiceTo?: string | null;
  region?: string[] | null;
  locationId?: string[] | null;
  providerId?: string[] | null;
  reportType?: string[] | null;
  specialtyId?: string[] | null;
  status?: string[] | null;
  jobMonth?: string[] | null;
  jobYear?: string[] | null;
  userId?: string[] | null;
  auditEvent?: string[] | null;
  numberOfDays?: number | null;
  dateOfUploadFrom?: string | null;
  dateOfUploadTo?: string | null;
  dateOfFaxFrom?: string | null;
  dateOfFaxTo?: string | null;
  patientName?: string | null;
  mrn?: string | null;
  clientNotes?: string | null;
};

export type TRegionPicklist = {
  regionId: string;
  regionName: string;
};

export type TAccount = TAccountPicklist & {
  accountId: string;
  accountName: string;
  accountStatus: AccountStatus;
  sendFromIndia: boolean;
  priority: number;
  tatInHours: number;
  effectiveDate?: Date;
  archiveAfter: number;
  enableFax: boolean;
  parentAccountId: string;
  passwordChangeNumDays: number;
  primaryLocationId?: number;
  sensitivity: number;
  removeBlankMarkers: boolean;
  blankMarkerStyle: TBlankMarkerStyle;
  hasCorePatientVisits: boolean;
  regions: Region[];
};

export type TLocationPicklist = {
  locationId: number;
  locationName: string;
  regionId: string;
};

export type TLocation = TLocationPicklist & {
  locationShortName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
  extLocationId: string;
  extLocationId2: string;
  phoneLocationId: number;
};

export type TContactGroupsDetail = {
  contactGroupId: string;
  contactId: number;
  accountId: string;
};

export type TContact = TContactPicklist & {
  accountId: string;
  isProvider: boolean;
  isVTUser?: boolean;
  phone: string;
  title: string;
  email: string;
  active: boolean;
  primaryLocation: string;
  primaryRegion: string;
};

export type TEditContact = TContactPicklist & {
  accountId?: string;
  locationId?: number | null;
  isProvider: boolean;
  firstName: string;
  middleInitial?: string | null;
  lastName: string;
  title: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  phone1: string;
  phone1Ext: string;
  phone2: string;
  phone2Ext: string;
  phone3: string;
  phone3Ext: string;
  fax: string;
  email: string;
  web: string;
  active: boolean;
  billingAccountName: string;
  locationName: string;
  provider?: Partial<TProvider> | null;
  regions: string[];
  contactGroups: string[];
};

export type TEditContactEmail = TContactPicklist & {
  accountId?: string;
  email: string;
};

export type TVTDialedNumberMap = {
  dialedNumber: string;
  tollFreeNumber: string;
};

export type TVTUser = {
  userId: string;
  accountId: string;
  password: string | null;
  contactId: number;
  defaultJobType: number;
  promptForMRN: boolean;
  defaultLocation: number | null;
  saveOnHangup: boolean;
  isOratorUser: boolean;
  playKey: string;
  recordKey: string;
  rewindKey: string;
  pauseKey: string;
  saveKey: string;
  forwardKey: string;
  eofKey: string;
  bofKey: string;
  abortKey: string;
  dialedNumber: string;
};

export type TProviderLocation = {
  accountId: string;
  providerId: number;
  locationId: number;
  seq: number;
  voxFileName: string;
};

enum TProviderStatus {
  Active = 65, // A
  Inactive = 73 // I
}

export type KeyFunction = {
  key: number | string;
  function: string;
  description: string;
};

export type TRefProvider = {
  rpId: number;
  rpName: string;
  accountId: string;
  practiceName: string;
  address1: string;
  address2: string;
  active: string;
  city: string;
  state: string;
  fax: string;
  extProviderId: string;
};

export type TEditRefProvider = {
  rpId: number;
  accountId?: string;
  practiceName: string;
  location?: string;
  rpFname: string;
  rpMinitial?: string;
  rpLname: string;
  credentials: string;
  title: string;
  sex: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  phone1: string;
  phone2: string;
  fax: string;
  email: string;
  active: string;
  extProviderId: string;
};

export type TProviderPicklist = {
  providerId: number;
  providerName: string;
};

export type TReportList = {
  groupName: string;
  reportName: string;
  reportObject: string;
  reportType: string;
};

export type TReportTypePicklist = {
  reportId: number;
  reportName: string;
  reportObject: string;
  reportType: string;
};

export type TDocumentTypePicklist = {
  documentTypeId: string;
  documentTypeName: string;
};

export type TDocumentType = TDocumentTypePicklist & {
  phoneId: string;
  clientPhoneId: string;
  description: string;
  clientDocumentTypeId: string;
};

export type TRegion = {
  regionId: number;
  regionName: string;
};

export type TReportParameters = {
  parameterName: string;
  multiSelect: string;
  dataType: string;
  description: string;
  defaultValue: any;
};

export type TTextControlJobDetails = {
  jobIds: string[];
  accountId: string;
  userId: string;
  jobAction: string;
  connectionId?: string | null;
  mergeDetails?: TMergeDetails | null;
};

export type TFaxSenderDetails = {
  accountName: string;
  displayName: string;
  phone1: string;
  fax: string;
  confidentialityNotice: string;
};

export type TNewFaxJob = {
  senderName: string;
  senderPhoneNumber: string;
  senderFaxNumber: string;
  senderCompanyName: string;
  recipientName: string;
  recipientPhoneNumber: string;
  recipientFaxNumber: string;
  recipientCompanyName: string;
  subject: string;
  notes: string;
  confidentialityNotice: string;
  jobIds: string[];
};

export type TMergeDetails = {
  isMerge?: boolean;
  noOfLines?: number;
};

export type TCurrentJobDetail = {
  jobId: number;
  vfId: number;
  voiceFileName: string | null;
  docFileName: string | null;
  deliveredTime: Date;
  estFileDate: Date;
  tat: string;
  jobStatus: string;
};

export type THistoryDetail = {
  userId: string;
  eventDatetime: Date;
  ipAddress: string;
  eventType: string;
  uploadPath: string | null;
};

export type TJobDetails = {
  firstName?: string;
  lastName?: string;
  middleInitial?: string;
  mrn?: string;
  visitId?: string;
  locationId?: number;
  reportType?: string;
  ProviderId?: number;
  dateOfService?: string;
  patientDOB?: string;
  clientNotes?: string;
  notesToClient?: string;
  approved?: number;
  vfId?: number;
  jobUnderReview?: boolean;
  jobStatus?: number;
  remoteAddress?: string | undefined;
  extension?: string;
  isEsignEnabled?: boolean;
  providerSignature?: string;
};

export type TVoiceFile = {
  vfId: number;
  voiceFileName: string;
};

export type TDocumentResponse = {
  blob: Blob;
  filename: string;
  contentLength: number;
};

export type TJobFilter = {
  jobStatus: JobStatus[];
  patientName: string;
  mrn: string;
  providerIds: string[];
  reportTypes: string[];
  dosFrom: string | null;
  dosTo: string | null;
  dodFrom: string | null;
  dodTo: string | null;
  jobApprovalStatus: number;
  regions: string[];
  locations: string[];
  patientDOB: string | null;
  vtJobId: number;
  qcHoldFilter: number;
};

export type TJob = {
  jobId: number;
  accountId: string;
  providerId: number;
  providerName: string;
  dateOfDictation: Date;
  visitDate: Date;
  patientName: string;
  mrn: string;
  docFileName: string;
  reportType: string;
  intReportType: string;
  printDownloadStatus: string;
  approved: number;
  locationShortName: string;
  notesFromClient: string;
  statFlag: string;
  isLocked: boolean;
  locationId: number;
  region: string;
  locationName: string;
  providerFirstName: string;
  providerMiddleInitial: string;
  providerLastName: string;
};

export type TContactPicklist = {
  contactId: number;
  contactName: string;
};

export type TContractGroupPicklist = {
  id: string;
  name: string;
};

export type TProvider = Partial<TProviderPicklist> & {
  version: number;
  accountId: string;
  firstName: string;
  lastName: string;
  middleInitial?: string | null;
  extProviderId: string;
  designation: string;
  credentials: string;
  specialityId: string;
  signature: string;
  providerStatus: TProviderStatus;
  dssId: string;
  phoneId: string;
  effectiveDate?: Date;
  providerType?: string;
  displayName: string;
  isEsignEnabled: boolean;
  locationId?: number | null;
  providerFolderName: string;
  providerLocations: number[];
  isVTUser?: boolean;
};

export type TSpecialityPicklist = {
  specialityId: string;
  description: string;
  shortName: string;
};

export type TCompletedTranscript = {
  jobId: number;
  docFileName: string;
  dateOfService: string;
  reportType: string;
  approved: string;
  deliveredTime: string;
  patientName: string;
  mrn: string;
};

export enum VoiceFileStatus {
  All = "All",
  New = "New",
  Downloaded = "Downloaded",
  Transcription = "Transcription",
  ProofReading = "ProofReading",
  Completed = "Completed",
  InProcess = "InProcess",
  PendingDownload = "PendingDownload"
}

export enum VoiceFileType {
  All = "All",
  Valid = "Valid",
  Aborted = "Aborted",
  Addendum = "Addendum",
  BlankPerDistributor = "BlankPerDistributor",
  BlankPerMT = "BlankPerMT",
  Incomplete = "Incomplete",
  Pullback = "Pullback",
  Repeat = "Repeat",
  Recall = "Recall"
}

export enum PrintDownloadStatus {
  Downloaded = 68, // D
  Hold = 72, // H
  New = 78 // N
}

export enum JobStatus {
  None = 0,
  QCHold = 1,
  Ready = 2,
  Archived = 4
}

export const JobStatusDescriptions: { [key in JobStatus]: string } = {
  [JobStatus.None]: "None",
  [JobStatus.QCHold]: "QC Hold",
  [JobStatus.Ready]: "Ready",
  [JobStatus.Archived]: "Archived"
};

export enum VoiceStatFlag {
  Regular = "Regular",
  Stat = "Stat",
  Priority = "Priority",
  R = "R",
  All = "All"
}

export enum ApprovedStatus {
  I = 73, //unknown status used in SP
  No = 78, // N
  Yes = 89, // Y
  InProcess = 79, // 0
  AutoApprovedAndUploaded = 86, // V
  ProviderApprovedAndUploaded = 87 // W
}

export enum Approved {
  No = "No",
  Yes = "Yes",
  InProcess = "InProcess",
  AutoApprovedAndUploaded = "AutoApprovedAndUploaded",
  ProviderApprovedAndUploaded = "ProviderApprovedAndUploaded"
}

export type TJobSummary = {
  vfId: number;
  voiceFileName: string;
  voiceFileLocation: string;
  dateOfDictation: string;
  duration: number;
  isVoiceFile: boolean;
  voiceFileStatus: VoiceFileStatus;
  statFlag: VoiceStatFlag;
  type: VoiceFileType;
  provider?: TProviderPicklist;
  voiceJobs?: TVoiceJob[];
  transcripts: TCompletedTranscript[];
};

export enum FaxJobStatus {
  Aborted = "Aborted",
  New = "New",
  Spooled = "Spooled",
  Inprogress = "Inprogress",
  Queued = "Queued",
  Error = "Error",
  Completed = "Completed"
}

export type TFaxJobDetail = {
  visitDate: string;
  mrn: string;
  patientName: string;
  providerName: string;
};

export type TFaxJobSummary = {
  faxJobId: number;
  createdByAccountId: string;
  createdByUserId: string;
  createdDateTime: string;
  status: FaxJobStatus;
  recipientName: string;
  recipientFaxNumber: string;
  numberOfPages: number;
  numRetries: number;
  lastUpdatedDateTime: string;
  lastError: string;
  details: TFaxJobDetail[];
};

export type TUserRole = {
  name: string;
};

export enum UserStatus {
  Active = "Active",
  Inactive = "Inactive"
}

export type TCiamUser = {
  id: string;
  creationType: string;
  userPrincipalName: string;
  userType: string;
  AccountEnabled: string;
  externalUserState: string;
  mail: string;
};

export type TUser = {
  userId: string;
  accountId: string;
  firstName: string;
  lastName: string;
  middleInitial: string;
  email: string;
  contactId: number;
  userStatus: UserStatus;
  ciamEmail?: string;
  primaryLocation: TLocationPicklist;
  authorizedRoles: string[];
  ciamUserId: string;
  ciamUser: TCiamUser;
  mobileApp: string;
};

export type TEditUser = {
  userId: string;
  accountId: string;
  contactId: number;
  landingPage: string;
  mobileApp: string;
  authorizedRoles: number[];
  authorizedLocations: number[];
  authorizedProviders: number[];
};

export type TRegisterUser = {
  accountId: string;
  userId: string;
  email: string;
  displayName: string;
  isInternalUser: boolean;
  password?: string;
};

export type TPrivilege = {
  id: number;
  name: string;
  description: string;
};

export type TRole = {
  roleId: number;
  accountId: string;
  description: string;
  lastUpdatedBy: string;
  lastUpdatedDatetime: string;
  privileges: TPrivilege[];
};

export type TVoiceFileExtensions = {
  fileExtension: string;
  isVoiceFile: boolean;
};

export enum UploadStatus {
  Ready = "Ready",
  Success = "Success",
  Failed = "Failed",
  Skipped = "Skipped",
  Uploading = "Uploading",
  Uploaded = "Uploaded"
}

export type TUploadJobFile = {
  uid: string;
  file: File;
  name: string;
  accountId?: string | null;
  providerId?: number | null;
  locationId?: number | null;
  isVoiceFile: boolean;
  uploadStatus: UploadStatus;
  dateOfService: string;
};

export type TAccountRolePrivileges = { roles: TRole[]; allPrivileges: TPrivilege[] };

export enum LandingPage {
  Inbox = "Inbox",
  QCHold = "QC Hold",
  SearchAdvanced = "Advanced Search"
}

export enum MobileApp {
  VScribe = "VScribe",
  DScribe = "DScribe"
}

export enum RegisterUserType {
  IntenalUser,
  Externaluser
}

export type Region = {
  regionId: string;
  regionName: string;
};

export type TVoiceJob = {
  jobId: number;
  vFStartTime: number;
  vFEndTime: number;
  isAddendum: string;
};

export enum Addendum {
  None = "None",
  Original = "Original",
  Final = "Final"
}

export enum DefaultDateType {
  None = "None",
  DOSCurrentWeek = "Date of Service - Current Week",
  DOSLastWeek = "Date of Service - Last Week",
  DOSCurrentMonth = "Date of Service - Current Month",
  DOSLastMonth = "Date of Service - Last Month",
  DODCurrentWeek = "Date of Dictation - Current Week",
  DODLastWeek = "Date of Dictation - Last Week",
  DODCurrentMonth = "Date of Dictation - Current Month",
  DODLastMonth = "Date of Dictation - Last Month"
}
